import type { LoginFormSubmitFn } from '../../form/LoginForm/LoginForm';

import { useQueryClient } from '@tanstack/react-query';
import { useCallback, useEffect, useMemo, useRef } from 'react';
import TagManager from 'react-gtm-module';
import { toast } from 'react-hot-toast';
import { useTranslation } from 'react-i18next';
import { Link, useNavigate, useSearchParams } from 'react-router';
import useLocalStorageState from 'use-local-storage-state';

import { fetchLogin } from '../../lib/flotiq-client/fetch-login';
import {
  getLoginErrorText,
  getMaskedOrganizationName,
  getTestProps,
} from '../../lib/helpers';

import LoginForm from '../../form/LoginForm/LoginForm';
import Authentication from '../../hoc/Authentication/Authentication';

const testId = 'login';

const Login = () => {
  const queryClient = useQueryClient();
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const activated = searchParams.get('activated');
  const deleted = searchParams.get('deleted');
  const status = searchParams.get('status');
  const [, setUser] = useLocalStorageState('cms.user');

  const toastRef = useRef<string>('');
  const loginSentRef = useRef<boolean>(false);

  const error = useMemo(
    () => getLoginErrorText(status, t, activated),
    [t, activated, status],
  );

  const navigateUrl = useMemo(() => {
    if (searchParams.get('redirect_uri')) {
      const searchParamsString = searchParams.toString();
      return '/' + (searchParamsString ? `?${searchParamsString}` : '');
    }
    return '/';
  }, [searchParams]);

  useEffect(() => {
    if (toastRef.current) {
      toast.remove(toastRef.current);
    }
    if (activated === 'true') {
      toastRef.current = toast.success(t('Activation.True'));
    } else if (deleted === 'true') {
      toastRef.current = toast.success(t('AccountDeleted.True'));
    } else if (deleted === 'false') {
      toastRef.current = toast.error(t('AccountDeleted.False'), {
        duration: Infinity,
      });
    } else if (error) {
      toastRef.current = toast.error(error, {
        duration: Infinity,
      });
    }
  }, [activated, deleted, error, t]);

  useEffect(() => {
    const tokenParam = searchParams.get('token');
    const registeredParam = searchParams.get('registered');

    if (tokenParam == null) return;
    try {
      const userData = window.atob(tokenParam);
      const userDataObject = JSON.parse(userData);
      setUser(userDataObject);

      if (!loginSentRef.current) {
        TagManager.dataLayer({
          dataLayer: {
            event: 'login',
            user_id: userDataObject.data?.id,
            organization_id: userDataObject.data?.organization,
            organization_name: getMaskedOrganizationName(
              userDataObject.data?.organization_name,
            ),
            plan_id: userDataObject.data?.limits_plan?.id,
            plan_name: userDataObject.data?.limits_plan?.name,
            space_id: userDataObject.data?.default_space?.id,
            user_roles: userDataObject.data?.roles,
          },
        });
        loginSentRef.current = true;
      }

      if (registeredParam)
        TagManager.dataLayer({
          dataLayer: {
            event: 'sign_up',
          },
        });

      navigate(navigateUrl);
    } catch {
      toastRef.current = toast.error(t('AppSumo.TokenNotValid'));
      navigate(navigateUrl);
    }
  }, [searchParams, navigate, t, navigateUrl, setUser]);

  useEffect(() => {
    queryClient.removeQueries({
      predicate: (query) =>
        query.queryKey[0] !== 'navigation_space' && query.queryKey.length > 2,
    });
  }, [queryClient]);

  const handlePost = useCallback<LoginFormSubmitFn>(
    async (values) => fetchLogin(values, setUser, t, navigate, navigateUrl),
    [setUser, t, navigate, navigateUrl],
  );

  return (
    <>
      <title>{t('Global.LogInToFlotiq')}</title>
      <Authentication
        authButtons={true}
        headerText={t('Global.LogInToFlotiq')}
        paragraphText={
          <>
            <span className="block text-sm md:text-base leading-6 mt-1">
              {t('Global.NewToFlotiq')}
              <Link to={`/${i18n.language}/register`} className="text-blue">
                <span> {t('Global.SignUp')}</span>
              </Link>
            </span>
          </>
        }
        {...getTestProps(testId, 'authentication', 'testId')}
      >
        <LoginForm
          buttonText={activated === 'true' ? t('Activation.True') : ''}
          error={error}
          onSubmit={handlePost}
          {...getTestProps(testId, 'form', 'testId')}
        />
      </Authentication>
    </>
  );
};

export default Login;
